import styled, { createGlobalStyle, css } from 'styled-components';
import customCss from '@/app/components/hotel-detail/css/customCss.ts';
import { HEADER_HEIGHT, SEARCH_WIDGET_HEIGHT, hasFixedExternalHeader } from '@/app/common/constants.ts';
import type { Brand } from '@/domainLogic/index.ts';
import { isIndexSelector, isListSelector, isMapSelector } from '@/app/common/css/selectors.ts';
import { descendantFocusRing } from '@/app/components/search/styles/mixins.ts';
import { LISTVIEW_CONTAINER_WIDTH, MAP_LIST_EVOLUTION } from '@/app/components/search/styles/constants.ts';

const indexStyles = css<{ evolutionEnabled: boolean }>`
  ${isIndexSelector} & {
    .hub-container {
      max-width: calc(100% - 32px) !important;
      padding-left: 0 !important;
      padding-right: 0 !important;

      @media (min-width: 768px) {
        max-width: calc(100% - 50px) !important;
      }

      @media (min-width: 1280px) {
        max-width: 1240px !important;
      }
    }
  }
`;

const mapStyles = css<{ evolutionEnabled: boolean }>`
  ${isMapSelector} & {
    .hub-container {
      max-width: calc(100% - 32px) !important;
      display: none;

      @media (min-width: 1024px) {
        max-width: ${({ evolutionEnabled }) =>
          evolutionEnabled ? `${MAP_LIST_EVOLUTION} !important` : '776px !important'};
        padding-left: 24px !important;
        padding-right: 24px !important;
        margin-left: 0;
        display: block;
      }

      @media (min-width: 1440px) {
        max-width: ${({ evolutionEnabled }) =>
          evolutionEnabled ? `${MAP_LIST_EVOLUTION} !important` : '876px !important'};
      }
    }
  }
`;

const listStyles = css<{ evolutionEnabled: boolean }>`
  ${isListSelector} & {
    .hub-container {
      max-width: calc(100% - 32px) !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
      margin-left: 16px;

      @media (min-width: 768px) {
        max-width: calc(100% - 50px) !important;
        margin-left: 25px;
      }

      @media (min-width: 1280px) {
        max-width: ${({ evolutionEnabled }) =>
          evolutionEnabled ? `${LISTVIEW_CONTAINER_WIDTH} !important` : '1150px !important'};
        margin: auto;
      }
    }

    max-width: 100%;
    overflow: hidden;
  }
`;

export const TsxContentHubFooter = styled.div<{ evolutionEnabled: boolean }>`
  ${mapStyles};
  ${listStyles};
  ${indexStyles};
`;

export const TsxMainContent = styled.div`
  /* fallback */
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  ${descendantFocusRing};
`;

export const TsxNavigation = styled.div``;

export const CSWExternalContainer = styled.div<{ brand: Brand }>`
  /* Mobile pinning is scoped to the brands whose header is viewport-fixed by its own
     CSS on every step (HP, see ExternalHeader) — including the evo detail ones, where
     HeaderContainer is static (no stacking context), so the z-index has to live here
     for the content not to scroll over the widget, and the backdrop keeps the band
     from being see-through once the container scrolls away. HG's header is injected
     in-flow (HtmlInjector) and scrolls with the page, so its CSW stays in flow too:
     pinning it would keep the widget floating over the content with a see-through
     strip where the header was. (MPH-864) */
  ${({ brand }) =>
    hasFixedExternalHeader(brand) &&
    css`
      position: fixed;
      z-index: 2;
      top: ${HEADER_HEIGHT(brand, true)}px;

      @media ${customCss.breakpoints.sm} {
        position: unset;
      }
    `};
  width: 100%;
  margin: auto;
  padding: 15px;
  height: ${SEARCH_WIDGET_HEIGHT}px;
  background: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};

  div,
  div:hover {
    box-shadow: none;
  }

  @media ${customCss.breakpoints.sm} {
    padding-left: 24px;
    padding-right: 24px;
  }

  @media ${customCss.breakpoints.md} {
    padding: 16px 36px 16px;
  }

  @media ${customCss.breakpoints.mdPlus} {
    display: flex;
    justify-content: center;
  }
`;

/**
 * Wraps the external header + CSW in HeaderManager. From tablet up the margin
 * clears the viewport-fixed brand header so the in-flow CSW starts under it, at
 * the same breakpoint the CSW switches to in-flow (CSWExternalContainer) and the
 * HeaderContainer heights change — a JS isMobile boundary here (768px) left a
 * 744–767px window where the CSW hid behind the header. HG's header is in-flow
 * (HtmlInjector), so it needs no clearing.
 */
export const ExternalHeaderSpacing = styled.div<{ brand: Brand; clearFixedHeader: boolean; isApp?: boolean }>`
  ${({ clearFixedHeader, brand, isApp }) =>
    clearFixedHeader &&
    css`
      @media ${customCss.breakpoints.sm} {
        margin-top: ${HEADER_HEIGHT(brand, false, isApp)}px;
      }
    `};
`;

/**
 * TEMPORARY anti-injection workaround (MPH-395), canonical doc for the whole
 * appShowCsw path. The app webview injects CSS hiding the ContentHub nav
 * (`[data-contenthub-id="navigation"]`), which contains the CSW; while that
 * injection exists we render the CSW standalone outside it and rebuild what the
 * header gave for free: flow space (no TopMargin), scroll pinning (decided by
 * NavigationManager), opaque bg. Remove the whole path when the app drops the
 * injection.
 */
export const CSWAppContainer = styled.div`
  position: relative;
  width: 100%;
  margin: auto;
  padding: 15px;
  height: ${SEARCH_WIDGET_HEIGHT}px;
  background: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};

  div,
  div:hover {
    box-shadow: none;
  }

  @media ${customCss.breakpoints.sm} {
    padding-left: 24px;
    padding-right: 24px;
  }

  @media ${customCss.breakpoints.md} {
    padding: 16px 36px 16px;
  }

  @media ${customCss.breakpoints.mdPlus} {
    display: flex;
    justify-content: center;
  }
`;

/**
 * MPH-862 (part of the MPH-395 workaround, see CSWAppContainer). The CSW lib
 * positions its expanded dialog and backdrop at `collapseConfiguration.topOffset`
 * (= the web header height, see useConfiguration) so they sit below the header.
 * In the app the header is hidden by the webview injection, so that offset is a
 * blank strip above the dialog. Passing `topOffset: 0` doesn't work: the lib
 * falls back to its 64px default on any falsy value (`topOffset.mobile || …`),
 * and the dialog lives in a body-level `.csw-portal`, out of CSWAppContainer's
 * reach — hence this global reset, rendered only on the appShowCsw path.
 */
export const CSWAppExpandedOffsetReset = createGlobalStyle`
  /* The dialog is nested inside the lib's ClickAway wrapper; the backdrop is a direct child. */
  .csw-portal .csw-expanded-wrapper {
    top: 0 !important;
    max-height: 100% !important;
  }

  .csw-portal > button {
    top: 0 !important;
  }
`;

/**
 * MPH-395 workaround (see CSWAppContainer). Pins the app CSW on non-evo pages,
 * mirroring the fixed non-evo web header. Must render outside TsxNavigation:
 * sticky only works within the parent's bounds, which end after the header.
 */
export const CSWAppStickyWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
`;

type HeaderContainerProps = {
  externalHeaderEnabled: boolean;
  showExternalCSW: boolean;
  brand: Brand;
  evolutionEnabled: boolean;
  staticHeader?: boolean;
  'data-fixed-header'?: string;
};

export const HeaderContainer = styled.div.attrs<HeaderContainerProps>(({ staticHeader }) => ({
  // scroll.css keys the modal-open header compensation on this attribute: only the fixed
  // variant needs its own padding — the static/in-flow header is already covered by the
  // body padding, and compensating it twice shifts the header content (MPH-793).
  'data-fixed-header': staticHeader ? undefined : '',
}))<HeaderContainerProps>`
  /* staticHeader keeps the header in the page flow so it scrolls away with the
     content. relative (not static) keeps the z-index stacking so the expanded
     CSW still overlays the page. The desktop fixed rules below are gated on
     !staticHeader rather than overridden: stylis emits @media blocks after the
     merged base rule, so a later plain declaration can never beat them. */
  position: ${({ staticHeader }) => (staticHeader ? 'relative' : 'fixed')};
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2;

  ${({ staticHeader }) =>
    staticHeader &&
    css`
      /* the inner ContentHub header has its own fixed positioning */
      > div {
        position: static;
      }
    `};

  ${({ evolutionEnabled, staticHeader }) =>
    evolutionEnabled &&
    css`
      header,
      [data-contenthub-id='navigation'] > div {
        /* remove box-shadow for CSW in evolution */
        box-shadow: none;
      }

      @media (max-width: 767px) {
        position: static;

        > div {
          position: static;
        }
      }

      ${
        !staticHeader &&
        css`
          @media (min-width: 1024px) {
            position: fixed;
            top: 0;
            left: 0;
            z-index: 3;

            // On desktop restore the inner component's own positioning.
            > div {
              position: unset;
            }
          }
        `
      };
    `};

  ${({ externalHeaderEnabled, showExternalCSW, brand, staticHeader }) =>
    externalHeaderEnabled &&
    css`
      background: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};
      z-index: 2 !important;
      height: ${showExternalCSW ? HEADER_HEIGHT(brand, true) + SEARCH_WIDGET_HEIGHT : HEADER_HEIGHT(brand, true)}px;

      ${isListSelector} & {
        box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.mediumDownShadow};
      }

      ${isIndexSelector} & {
        box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.mediumDownShadow};

        @media ${customCss.breakpoints.lg} {
          box-shadow: none;
        }
      }

      @media ${customCss.breakpoints.sm} {
        height: ${showExternalCSW ? HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT : HEADER_HEIGHT(brand, false)}px;
      }

      @media ${customCss.breakpoints.mdPlus} {
        height: ${showExternalCSW ? HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT : HEADER_HEIGHT(brand, false)}px;

        ${isListSelector} & {
          height: ${
            showExternalCSW ? HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT : HEADER_HEIGHT(brand, false)
          }px;
        }
      }

      ${
        staticHeader &&
        css`
          /* In-flow header (evo detail steps), tablet up: ExternalHeaderSpacing already
             clears the viewport-fixed brand header (and collapses out of this container),
             and from tablet up the CSW is in flow — so the fixed-path header+widget
             height above would leave a widget-sized empty band under the CSW (MPH-864).
             Same breakpoint as the CSW's position switch in CSWExternalContainer.
             Mobile keeps it: there the CSW is viewport-fixed and this height is its flow
             placeholder. Must come after the media heights above — same specificity, so
             the later rule wins. */
          @media ${customCss.breakpoints.sm} {
            height: auto;
          }
        `
      };
    `};
`;
